import { useEffect, useRef, useState } from "react";
import { Loader2, RotateCcw, Plus, Trash2, ImagePlus, X, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import { Drawer } from "@/components/layout/Drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Field, DrawerSection } from "@/components/forms/fields";
import { AuthImage } from "@/components/kit/AuthImage";
import { returnsService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";

const EMPTY = { store_id: "", tracking_number: "", order_reference: "", notes: "" };
const EMPTY_PRODUCT = { sku: "", product_name: "", quantity: 1 };
const ACCEPT = ["jpg", "jpeg", "png", "webp"];
const MAX_BYTES = 10 * 1024 * 1024;

function ImagesSection({ returnId, images, readOnly, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState(null);

  const validate = (files) => {
    const valid = [];
    for (const f of files) {
      const ext = f.name.split(".").pop().toLowerCase();
      if (!ACCEPT.includes(ext)) { toast.error(`${f.name}: use JPG, PNG or WebP`); continue; }
      if (f.size > MAX_BYTES) { toast.error(`${f.name} exceeds 10MB`); continue; }
      valid.push(f);
    }
    return valid;
  };

  const upload = async (fileList) => {
    const files = validate(Array.from(fileList));
    if (files.length === 0) return;
    setUploading(true);
    try {
      const res = await returnsService.uploadImages(returnId, files);
      onChange(res.return.images || []);
      toast.success(`Uploaded ${res.added?.length || files.length} image(s)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (path) => {
    try {
      const res = await returnsService.deleteImage(returnId, path);
      onChange(res.return.images || []);
      toast.success("Image removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <DrawerSection title={`Images${images.length ? ` (${images.length})` : ""}`}>
      {!readOnly && (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files); }}
          data-testid="return-image-dropzone"
          className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${dragging ? "border-primary bg-brand-light" : "border-border hover:border-primary/40 hover:bg-muted/40"}`}
        >
          {uploading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : <ImagePlus className="h-5 w-5 text-muted-foreground" />}
          <p className="text-sm font-medium text-foreground">Drag &amp; drop or click to upload</p>
          <p className="text-xs text-muted-foreground">JPG, PNG, WebP · up to 10MB · multiple allowed</p>
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden"
            data-testid="return-image-input" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
        </div>
      )}
      {images.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4">
          {images.map((img) => (
            <div key={img.path} className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-muted/40" data-testid="return-image-thumb">
              <AuthImage path={img.path} alt={img.filename} className="h-full w-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                <button type="button" onClick={() => setPreview(img)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-foreground" aria-label="View image" data-testid="return-image-view">
                  <Maximize2 className="h-4 w-4" />
                </button>
                {!readOnly && (
                  <button type="button" onClick={() => remove(img.path)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-error" aria-label="Remove image" data-testid="return-image-remove">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {images.length === 0 && readOnly && <p className="text-sm text-muted-foreground">No images uploaded.</p>}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl p-2" data-testid="return-image-preview">
          {preview && <AuthImage path={preview.path} alt={preview.filename} className="max-h-[80vh] w-full rounded-lg object-contain" />}
        </DialogContent>
      </Dialog>
    </DrawerSection>
  );
}

export function ReturnDrawer({ open, onOpenChange, record, stores = [], writable = true, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [products, setProducts] = useState([{ ...EMPTY_PRODUCT }]);
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const isEdit = !!record;
  const readOnly = !writable || (isEdit && record?.status === "confirmed");

  useEffect(() => {
    if (open) {
      if (record) {
        setForm({
          store_id: record.store_id || "",
          tracking_number: record.tracking_number || "",
          order_reference: record.order_reference || "",
          notes: record.notes || "",
        });
        setProducts(record.products?.length ? record.products.map((p) => ({ sku: p.sku, product_name: p.product_name || "", quantity: p.quantity })) : [{ ...EMPTY_PRODUCT }]);
        setImages(record.images || []);
      } else {
        setForm(EMPTY);
        setProducts([{ ...EMPTY_PRODUCT }]);
        setImages([]);
      }
    }
  }, [open, record]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const setProduct = (i, key, value) => setProducts((ps) => ps.map((p, idx) => (idx === i ? { ...p, [key]: value } : p)));
  const addProduct = () => setProducts((ps) => [...ps, { ...EMPTY_PRODUCT }]);
  const removeProduct = (i) => setProducts((ps) => (ps.length > 1 ? ps.filter((_, idx) => idx !== i) : ps));

  const validProducts = products.filter((p) => p.sku.trim() && Number(p.quantity) > 0);
  const storeError = !form.store_id ? "Store is required" : "";
  const productsError = validProducts.length === 0 ? "Add at least one product with a SKU and quantity" : "";
  const canSave = !storeError && !productsError && !saving && !readOnly;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const body = {
        store_id: form.store_id,
        tracking_number: form.tracking_number,
        order_reference: form.order_reference,
        notes: form.notes,
        products: validProducts.map((p) => ({ sku: p.sku.trim(), product_name: (p.product_name || "").trim(), quantity: Number(p.quantity) })),
      };
      if (isEdit) { await returnsService.update(record.id, body); toast.success("Return updated"); }
      else { await returnsService.create(body); toast.success("Return created — add images from the return's Edit view"); }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const storeName = stores.find((s) => s.id === form.store_id)?.store_name;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      icon={RotateCcw}
      title={isEdit ? `${readOnly ? "View" : "Edit"} ${record?.return_id || "Return"}` : "New Return"}
      description={isEdit ? "Return details and images." : "Log a returned shipment. Save first, then add images."}
      widthClass="sm:max-w-xl"
      footer={
        readOnly ? (
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="return-drawer-close">Close</Button>
        ) : (
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="return-drawer-cancel">Cancel</Button>
            <Button onClick={handleSave} disabled={!canSave} data-testid="return-drawer-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Create return"}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-6">
        <DrawerSection title="Return details">
          <Field label="Store" required error={storeError}>
            {isEdit ? (
              <Input value={storeName || "—"} disabled data-testid="return-store-readonly" />
            ) : (
              <Select value={form.store_id} onValueChange={set("store_id")}>
                <SelectTrigger data-testid="return-store-select"><SelectValue placeholder="Select a store" /></SelectTrigger>
                <SelectContent>{stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.store_name}</SelectItem>))}</SelectContent>
              </Select>
            )}
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Tracking Number"><Input value={form.tracking_number} onChange={(e) => set("tracking_number")(e.target.value)} disabled={readOnly} data-testid="return-tracking-input" /></Field>
            <Field label="Order Reference" hint="Optional"><Input value={form.order_reference} onChange={(e) => set("order_reference")(e.target.value)} disabled={readOnly} data-testid="return-order-ref-input" /></Field>
          </div>
          <Field label="Notes"><Textarea value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={2} disabled={readOnly} data-testid="return-notes-input" /></Field>
        </DrawerSection>

        <DrawerSection title="Products">
          <div className="space-y-3">
            {products.map((p, i) => (
              <div key={i} className="grid grid-cols-12 items-end gap-2" data-testid={`return-product-${i}`}>
                <div className="col-span-4">
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">SKU</label>
                  <Input value={p.sku} onChange={(e) => setProduct(i, "sku", e.target.value)} disabled={readOnly} placeholder="FF-100" data-testid={`return-product-sku-${i}`} />
                </div>
                <div className="col-span-5">
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Product</label>
                  <Input value={p.product_name} onChange={(e) => setProduct(i, "product_name", e.target.value)} disabled={readOnly} placeholder="Widget" data-testid={`return-product-name-${i}`} />
                </div>
                <div className="col-span-2">
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Qty</label>
                  <Input type="number" min={1} value={p.quantity} onChange={(e) => setProduct(i, "quantity", e.target.value)} disabled={readOnly} data-testid={`return-product-qty-${i}`} />
                </div>
                <div className="col-span-1">
                  {!readOnly && (
                    <button type="button" onClick={() => removeProduct(i)} disabled={products.length === 1}
                      className="flex h-10 w-full items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-error-light hover:text-error disabled:opacity-40"
                      data-testid={`return-product-remove-${i}`} aria-label="Remove product">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {productsError && !readOnly && <p className="text-xs font-medium text-error-dark">{productsError}</p>}
          {!readOnly && (
            <Button variant="outline" size="sm" className="mt-1 gap-2" onClick={addProduct} data-testid="return-add-product">
              <Plus className="h-4 w-4" /> Add product
            </Button>
          )}
        </DrawerSection>

        {isEdit ? (
          <ImagesSection returnId={record.id} images={images} readOnly={readOnly} onChange={(imgs) => { setImages(imgs); onSaved?.(); }} />
        ) : (
          <div className="rounded-xl border border-dashed border-border px-4 py-4 text-sm text-muted-foreground" data-testid="return-images-locked">
            Save the return first, then reopen it to attach images.
          </div>
        )}
      </div>
    </Drawer>
  );
}
